<template>
  <tr class="delivery-start-row-component" :class="{ 'hover-row': hoverRow }" @mouseenter="hoverRow = true"
    @mouseleave="hoverRow = false">
    <td colspan="3">{{ `Tur ${trip_name} start` }}</td>
    <td colspan="1">Total: {{ total_quantity }}</td>
    <td colspan="1"></td>
    <td colspan="1">{{ box_count }} {{ vehicleCapacityCategory && '/ '+vehicleCapacityCategory.capacity_limit_full }} <span class="interval-indicator" :style="capacityIndicatorStyle"></span>
    </td>
    <td colspan="4"></td>
    <td class="is-lockable" colspan="4">Pak bil</td>
    <td class="is-lockable">
      <time-picker v-model="activityStart" :tabindex="100 * tripIndex + 1" />
    </td>
    <td class="is-lockable">
      <duration-picker v-model="activityDuration" :tabindex="100 * tripIndex + 2" />
    </td>
    <td class="is-lockable">
      <time-picker v-model="activityEnd" :tabindex="100 * tripIndex + 3" />
    </td>
    <th class="column-delimiter"></th>
    <td class="is-lockable">
      <select v-model="first_order.vehicle_id" @change="vehicleChange()">
        <option :value="null"></option>
        <option v-for="vehicle in vehicles" :key="vehicle.id" :value="vehicle.id" :disabled="in_use_vehicles.includes(vehicle.id)">{{ vehicle.title }}</option>
      </select>
    </td>
    <td class="is-lockable">
      <select v-model="first_order.driver_id" @change="driverChange()">
        <option :value="null"></option>
        <option v-for="driver in drivers" :key="driver.id" :value="driver.id" :disabled="in_use_drivers.includes(driver.id)">{{ driver.name }}</option>
      </select>
    </td>
  </tr>
</template>

<script>
import moment from 'moment/moment';
import { computed, ref } from 'vue';
import { useRouteDataStore } from '../stores/route-data';
import DurationPicker from './DurationPicker.vue';
import TimePicker from './TimePicker.vue';

export default {
  components: { DurationPicker, TimePicker },
  props: {
    trip_name: {
      type: String,
      required: true,
    },
    first_order: {
      type: Object,
      required: true,
    },
    vehicles: {
      type: Array,
      required: true
    },
    vehicle_capacity_categories: {
      type: Array,
      required: true
    },
    drivers: {
      type: Array,
      required: true
    },
    total_quantity: {
      type: Number,
      required: true
    },
    box_count: {
      type: Number,
      required: true
    },
    in_use_drivers: {
      type: Array,
      required: true,
    },
    in_use_vehicles: {
      type: Array,
      required: true,
    },
  },
  setup(props, { emit }) {
    const routeDataStore = useRouteDataStore();
    const hoverRow = ref(false);

    const id = computed(() => {
      return `${props.trip_name}_start`;
    });

    const tempActivityStart = ref(null);
    const activityStart = computed({
      get() {
        // console.log('activityStart:get', tempActivityStart.value, tempActivityEnd.value);
        if (activityEnd.value && activityDuration.value !== null) {
          // console.log('resetting tempActivityStart');
          tempActivityStart.value = null;
          return moment(activityEnd.value, 'HH:mm').subtract(activityDuration.value, 'minutes').format('HH:mm');
        } else if (tempActivityStart.value !== null) {
          return tempActivityStart.value;
        } else {
          return null;
        }
      },
      set(newValue) {
        // console.log('activityStart:set', newValue);
        const firstOrderHasStart = props.first_order && props.first_order.driveStart;
        const durationIsSet = activityDuration.value !== null;
        if (firstOrderHasStart && durationIsSet) {
          if (activityStart.value && newValue) {
            const diff = moment(newValue, 'HH:mm').diff(moment(activityStart.value, 'HH:mm'), 'minutes');
            const newEndValue = moment(activityEnd.value, 'HH:mm').add(diff, 'minutes').format('HH:mm');
            props.first_order.driveStart = newEndValue;
          }
        } else {
          // console.log('tempActivityStart', newValue, JSON.stringify(newValue));
          if (newValue) {
            tempActivityStart.value = newValue;
            if (durationIsSet) {
              activityEnd.value = moment(newValue, 'HH:mm').add(activityDuration.value, 'minutes').format('HH:mm');
            }
          } else {
            tempActivityStart.value = null;
          }
        }
      }
    });

    const activityDuration = computed({
      get() {
        if (Object.hasOwn(routeDataStore.routeData.service_durations, id.value)) {
          return routeDataStore.routeData.service_durations[id.value];
        } else {
          return 10;
        }
      },
      set(newValue) {
        if (activityDuration.value !== null && newValue !== null && activityEnd.value) {
          const diff = newValue - activityDuration.value;
          const newEndValue = moment(activityEnd.value, 'HH:mm').add(diff, 'minutes').format('HH:mm');
          routeDataStore.routeData.service_durations[id.value] = newValue;
          props.first_order.driveStart = newEndValue;
        } else {
          routeDataStore.routeData.service_durations[id.value] = newValue;
        }
      }
    });

    const tempActivityEnd = ref(null);
    const activityEnd = computed({
      get() {
        // console.log('activityEnd:get', tempActivityStart.value, tempActivityEnd.value);
        if (props.first_order && props.first_order.driveStart) {
          tempActivityEnd.value = null;
          return props.first_order.driveStart;
        } else if (tempActivityEnd.value !== null) {
          return tempActivityEnd.value;
        } else {
          return null;
        }
      },
      set(newValue) {
        // console.log('activityEnd:set', newValue);
        const firstOrderHasStart = props.first_order && props.first_order.driveStart;
        if (firstOrderHasStart && newValue) {
          props.first_order.driveStart = newValue;
        } else {
          tempActivityEnd.value = newValue ? newValue : null;
          props.first_order.driveStart = tempActivityEnd.value;
        }
      }
    });

    const tripNames = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];
    const tripIndex = computed(() => {
      return tripNames.indexOf(props.trip_name);
    });

    const vehicleChange = () => {
      emit('vehicleChange', props.first_order.vehicle_id);
    };
    const driverChange = () => {
      emit('driverChange', props.first_order.driver_id);
    };

    const vehicleCapacityCategory = computed(() => {
      const selectedVehicleId = props.first_order.vehicle_id;
      const selectedVehicle = props.vehicles.find(vehicle => vehicle.id === selectedVehicleId);
      const category = props.vehicle_capacity_categories.find(category => category.id === selectedVehicle?.vehicle_capacity_category_id)
      return category;
    })


    const capacityIndicatorStyle = computed(() => {
      const color = {
        red: '#dc3545',
        yellow: '#ffc107',
        green: '#28a745',
        transparent: 'transparent'
      };

      let colorKey = null;

      const capacityLimitLow = vehicleCapacityCategory?.capacity_limit_low;
      const capacityLimitFull = vehicleCapacityCategory?.capacity_limit_full;

      if (props.box_count >= capacityLimitFull) {
        colorKey = "red";
        props.first_order.setCapacityCategoryErrorsErrors('red', 1)
      } else if (props.box_count >= capacityLimitLow) {
        colorKey = "yellow";
        props.first_order.setCapacityCategoryErrorsErrors('yellow', 1)
      } else if (props.box_count < capacityLimitLow) {
        colorKey = "green";
        props.first_order.setCapacityCategoryErrorsErrors('green', 1)
      } else {
        colorKey = 'transparent';
      }

      return {
        backgroundColor: color[colorKey],
      };
    });

    return {
      hoverRow,
      activityStart,
      activityDuration,
      activityEnd,
      vehicleChange,
      driverChange,
      tripIndex,
      capacityIndicatorStyle,
      vehicleCapacityCategory
    };
  }
}
</script>

<style lang="scss" scoped>
.delivery-start-row-component {
  transition: background-color 0.25s ease, border-color 0.25s ease;
  background-color: #f0f0f0;

  &.hover-row {
    background-color: #b8daff;
    border-color: #7abaff;
  }

  td {
    vertical-align: middle;
  }

  .interval-indicator {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-left: 5px;
  }
}
</style>